import * as THREE from 'three';
import Enemy from './Enemy.js';
import { ENEMY_TYPES, SPAWN_WAVES } from './EnemyConfig.js';

export default class EnemyManager {
    constructor(scene, physics, audioManager, bus) {
        this.scene = scene;
        this.physics = physics;
        this.audioManager = audioManager;
        this.bus = bus;

        this.enemies = [];
        this.flatZoneRadius = 80;

        // Wave timers — one per SPAWN_WAVES entry
        this.waveTimers = SPAWN_WAVES.map((wave) => ({
            wave,
            // Time until next wave spawns (start with a short initial delay)
            nextSpawnIn: this._randomBetween(8, 20),
            // Active wave tracking: when enemies from this wave should leave
            lingerDeadline: null,
            // Which type was picked for the current active wave
            activeType: null,
        }));

        // Event handlers
        this._onEnemyKilled = (e) => {
            const scoreValue = e.detail?.scoreValue || 100;
            const game = this.scene?.userData?.game;
            if (game?.scoreManager) {
                game.scoreManager.addScore(scoreValue, 'enemy_kill');
            }
        };

        this._onEnemyAttack = (e) => {
            const bus = this.bus;
            if (bus && typeof bus.fuel === 'number') {
                bus.fuel = Math.max(0, bus.fuel - (e.detail?.fuelDamage || 3));
            }
        };

        window.addEventListener('enemy_killed', this._onEnemyKilled);
        window.addEventListener('enemy_attack', this._onEnemyAttack);
    }

    initialize() {
        // Nothing spawns immediately — first wave arrives after the initial timer
    }

    // ── Spawning ─────────────────────────────────────────────────────────

    spawnEnemy(type) {
        const config = ENEMY_TYPES[type];
        if (!config) return null;

        const busPos = this.bus?.mesh?.position || new THREE.Vector3(0, 0, 0);
        const heightMap = this.scene?.userData?.terrainHeightMap;

        let position = null;
        let attempts = 0;

        while (!position && attempts < 20) {
            attempts++;
            const angle = Math.random() * Math.PI * 2;
            const dist = config.spawnMinDist +
                Math.random() * (config.spawnMaxDist - config.spawnMinDist);
            const x = busPos.x + Math.cos(angle) * dist;
            const z = busPos.z + Math.sin(angle) * dist;

            if (Math.sqrt(x * x + z * z) < this.flatZoneRadius) continue;

            const halfSize = 900;
            if (Math.abs(x) > halfSize || Math.abs(z) > halfSize) continue;

            const terrainY = heightMap ? heightMap.getHeight(x, z) : 0;
            const y = terrainY + (config.flyHeight || 0);
            position = new THREE.Vector3(x, y, z);
        }

        if (!position) return null;

        const enemy = new Enemy(this.scene, config, position);
        enemy._type = type;
        this.enemies.push(enemy);
        return enemy;
    }

    /**
     * Spawn a wave — pick a random type from the pool and spawn a group.
     * Returns the chosen type so the timer can track it for departure.
     */
    spawnWave(waveConfig) {
        // Pick a random type from the pool
        const types = waveConfig.types || [waveConfig.type];
        const type = types[Math.floor(Math.random() * types.length)];

        // Don't exceed maxActive total enemies
        const currentAlive = this.enemies.filter(
            e => e.isAlive && !e.isDeparting
        ).length;
        const canSpawn = Math.max(0, (waveConfig.maxActive || 2) - currentAlive);
        const toSpawn = Math.min(waveConfig.count, canSpawn);

        for (let i = 0; i < toSpawn; i++) {
            this.spawnEnemy(type);
        }
        return type;
    }

    // ── Update Loop ──────────────────────────────────────────────────────

    update(deltaTime) {
        if (!this.bus?.mesh) return;
        const busPos = this.bus.mesh.position;

        // Update wave timers — handle spawning & departing
        this._updateWaveTimers(deltaTime);

        // Update and cull individual enemies
        for (let i = this.enemies.length - 1; i >= 0; i--) {
            const enemy = this.enemies[i];

            // Remove dead
            if (!enemy.isAlive) {
                enemy.dispose();
                this.enemies.splice(i, 1);
                continue;
            }

            // Despawn if too far
            if (enemy.mesh) {
                const dist = enemy.mesh.position.distanceTo(busPos);
                const despawnDist = ENEMY_TYPES[enemy._type]?.despawnDist || 600;
                if (dist > despawnDist) {
                    enemy.dispose();
                    this.enemies.splice(i, 1);
                    continue;
                }
            }

            // Remove departed enemies that have flown out of sight
            if (enemy.isDeparted) {
                enemy.dispose();
                this.enemies.splice(i, 1);
                continue;
            }

            enemy.update(deltaTime, busPos);
        }
    }

    _updateWaveTimers(deltaTime) {
        for (const wt of this.waveTimers) {
            // If a wave is currently lingering, check if it's time to leave
            if (wt.lingerDeadline !== null) {
                wt.lingerDeadline -= deltaTime;
                if (wt.lingerDeadline <= 0) {
                    // Tell all current enemies to fly away
                    this._departAll();
                    wt.lingerDeadline = null;
                    wt.activeType = null;
                    // Schedule next wave
                    const [minI, maxI] = wt.wave.spawnInterval;
                    wt.nextSpawnIn = this._randomBetween(minI, maxI);
                }
            } else {
                // Counting down to next wave spawn
                wt.nextSpawnIn -= deltaTime;
                if (wt.nextSpawnIn <= 0) {
                    wt.activeType = this.spawnWave(wt.wave);
                    // Set linger duration
                    const [minL, maxL] = wt.wave.lingerDuration;
                    wt.lingerDeadline = this._randomBetween(minL, maxL);
                }
            }
        }
    }

    /**
     * Tell all alive enemies to depart (fly up and away).
     */
    _departAll() {
        for (const enemy of this.enemies) {
            if (enemy.isAlive && !enemy.isDying && !enemy.isDeparting) {
                enemy.depart();
            }
        }
    }

    // ── Helpers ──────────────────────────────────────────────────────────

    _randomBetween(min, max) {
        return min + Math.random() * (max - min);
    }

    clear() {
        for (const enemy of this.enemies) {
            enemy.dispose();
        }
        this.enemies = [];
        window.removeEventListener('enemy_killed', this._onEnemyKilled);
        window.removeEventListener('enemy_attack', this._onEnemyAttack);
    }
}
